import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { addSubscription } from "@/lib/feedReader";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string | null;
  onAdded: () => void;
  /** Current count of subscriptions in this workspace, for at-cap messaging. */
  currentCount?: number;
  /** Per-workspace cap from tier entitlements. */
  cap?: number;
};

export const AddFeedDialog = ({ open, onOpenChange, workspaceId, onAdded, currentCount, cap }: Props) => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const atCap = typeof cap === "number" && typeof currentCount === "number" && currentCount >= cap;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceId || !url.trim()) return;
    setBusy(true);
    try {
      const result = await addSubscription(workspaceId, url.trim());
      toast.success(`Added “${result.subscription.title}” (${result.imported} item${result.imported === 1 ? "" : "s"} imported)`);
      setUrl("");
      onOpenChange(false);
      onAdded();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add a feed</DialogTitle>
            <DialogDescription>
              Paste an RSS or Atom feed URL. We&rsquo;ll fetch the latest items right away and refresh the feed once a day.
            </DialogDescription>
          </DialogHeader>
          {atCap ? (
            <div className="space-y-3 py-4">
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-3 text-sm text-amber-700 dark:text-amber-300">
                You&rsquo;ve reached this workspace&rsquo;s feed cap of <strong>{cap}</strong>. Remove a feed below, or upgrade for more.
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
                <Button asChild>
                  <Link to="/pricing/" onClick={() => onOpenChange(false)}>See plans</Link>
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2 py-4">
                <Label htmlFor="feed-url">Feed URL</Label>
                <Input
                  id="feed-url"
                  type="url"
                  required
                  autoFocus
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/feed.xml"
                  disabled={busy}
                />
                <p className="text-xs text-muted-foreground">
                  Tip: many sites publish their feed at <code>/feed</code>, <code>/rss</code>, or <code>/atom.xml</code>.
                  {typeof cap === "number" && typeof currentCount === "number" && (
                    <> You&rsquo;re using <strong>{currentCount}</strong> of <strong>{cap}</strong> feeds.</>
                  )}
                </p>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy || !url.trim() || !workspaceId}>
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Add feed
                </Button>
              </DialogFooter>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
